// Demo Mode Settings Tab
import React, { useState, useEffect } from "react";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "";

const DemoModeSettings = () => {
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  useEffect(() => {
    fetchDemoConfig();
  }, []);

  const fetchDemoConfig = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/platform-settings/demo-config`);
      if (res.ok) {
        const data = await res.json();
        setIsDemoMode(data.isDemoMode === true);
      }
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (newValue) => {
    setSaving(true);
    setMessage({ type: "", text: "" });
    try {
      const token = localStorage.getItem("ogestyle_token");
      const res = await fetch(`${API_BASE}/api/admin/demo-mode`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ isDemoMode: newValue }),
      });

      if (res.ok) {
        const data = await res.json();
        setIsDemoMode(data.isDemoMode);
        setMessage({
          type: "success",
          text: `Demo mode ${data.isDemoMode ? "enabled" : "disabled"} successfully.`,
        });
      } else {
        const err = await res.json().catch(() => ({}));
        setMessage({
          type: "error",
          text: err.message || "Failed to update demo mode.",
        });
      }
    } catch (err) {
      setMessage({ type: "error", text: "Network error. Please try again." });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <div className="spinner"></div>
        <p>Loading demo settings...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Status message */}
      {message.text && (
        <div
          style={{
            padding: "12px 16px",
            borderRadius: "8px",
            marginBottom: "20px",
            background: message.type === "success" ? "#f0fdf4" : "#fef2f2",
            color: message.type === "success" ? "#166534" : "#dc2626",
            border: `1px solid ${message.type === "success" ? "#bbf7d0" : "#fecaca"}`,
            fontSize: "14px",
          }}
        >
          {message.text}
        </div>
      )}

      {/* Demo mode toggle card */}
      <div
        style={{
          background: "white",
          border: `2px solid ${isDemoMode ? "#f59e0b" : "#e5e7eb"}`,
          borderRadius: "12px",
          padding: "32px",
          marginBottom: "24px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "24px",
            flexWrap: "wrap",
          }}
        >
          <div style={{ flex: 1 }}>
            <h2
              style={{
                fontSize: "20px",
                fontWeight: "600",
                marginBottom: "8px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              🧪 Demo Mode
              {isDemoMode && (
                <span
                  style={{
                    background: "#f59e0b",
                    color: "white",
                    fontSize: "12px",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    fontWeight: "600",
                  }}
                >
                  ACTIVE
                </span>
              )}
            </h2>
            <p
              style={{
                color: "#6b7280",
                fontSize: "14px",
                lineHeight: "1.6",
                marginBottom: "0",
              }}
            >
              When enabled, the platform runs in demonstration mode. All
              visitors see a guided tooltip overlay and a sticky banner with the
              test card details. SMS notifications are suppressed. All bookings,
              orders, and new user accounts created while demo mode is active
              are flagged as demo data and automatically purged after 7 days.
            </p>
          </div>

          {/* Toggle */}
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "8px" }}>
            <button
              onClick={() => handleToggle(!isDemoMode)}
              disabled={saving}
              style={{
                width: "64px",
                height: "34px",
                borderRadius: "17px",
                border: "none",
                background: isDemoMode ? "#f59e0b" : "#d1d5db",
                cursor: saving ? "not-allowed" : "pointer",
                position: "relative",
                transition: "background 0.2s",
                opacity: saving ? 0.6 : 1,
              }}
              aria-label={isDemoMode ? "Disable demo mode" : "Enable demo mode"}
            >
              <span
                style={{
                  position: "absolute",
                  top: "3px",
                  left: isDemoMode ? "33px" : "3px",
                  width: "28px",
                  height: "28px",
                  borderRadius: "50%",
                  background: "white",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                  transition: "left 0.2s",
                }}
              />
            </button>
            <span style={{ fontSize: "12px", color: "#6b7280" }}>
              {saving ? "Saving..." : isDemoMode ? "On" : "Off"}
            </span>
          </div>
        </div>
      </div>

      {/* What happens info box */}
      <div
        style={{
          background: "#fffbeb",
          border: "1px solid #fde68a",
          borderRadius: "12px",
          padding: "24px",
          marginBottom: "24px",
        }}
      >
        <h3
          style={{ fontSize: "16px", fontWeight: "600", marginBottom: "12px" }}
        >
          ⚠️ What demo mode does
        </h3>
        <ul
          style={{
            margin: 0,
            paddingLeft: "20px",
            fontSize: "14px",
            color: "#374151",
            lineHeight: "2",
          }}
        >
          <li>Shows a sticky banner at the top of every page with the Stripe test card</li>
          <li>Shows per-page tooltip guides to walk visitors through the platform</li>
          <li>Suppresses all outbound SMS notifications</li>
          <li>
            Stamps all new bookings, orders, and user accounts as demo data
            (flagged for cleanup)
          </li>
          <li>
            Runs an automatic nightly cleanup that purges demo records older
            than 7 days and anonymises user accounts
          </li>
          <li>Real payment processing still goes through Stripe test mode</li>
        </ul>
      </div>

      {/* Test card reference */}
      <div
        style={{
          background: "#1e293b",
          color: "white",
          borderRadius: "12px",
          padding: "24px",
        }}
      >
        <h3
          style={{
            fontSize: "16px",
            fontWeight: "600",
            marginBottom: "16px",
            color: "#f1f5f9",
          }}
        >
          💳 Stripe Test Card (shown to demo visitors)
        </h3>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: "16px",
          }}
        >
          {[
            { label: "Card Number", value: "4242 4242 4242 4242" },
            { label: "Expiry", value: "12 / 34" },
            { label: "CVV", value: "567" },
            { label: "ZIP", value: "12345" },
          ].map(({ label, value }) => (
            <div key={label}>
              <div style={{ fontSize: "11px", color: "#94a3b8", marginBottom: "4px" }}>
                {label}
              </div>
              <div
                style={{
                  fontFamily: "monospace",
                  fontSize: "15px",
                  fontWeight: "600",
                  color: "#f1f5f9",
                  letterSpacing: "0.05em",
                }}
              >
                {value}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DemoModeSettings;
